import React from 'react';
import { observer } from 'mobx-react';
import { observable, when, computed } from 'mobx';
import { AppUI } from '@seedlang/state';
import Form from 'components/form_factory/form';
import FormFactory from 'components/form_factory/form_factory';
import styled from '@emotion/styled';
import Text from 'components/text';
import OutlineButton from 'components/button/outline_button';
import { Theme, Constants } from '@seedlang/constants';
import Alert from 'components/alert';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import { isPresent, isBlank } from '@seedlang/utils';
import SettingsUserLanguage from './settings_user_language';
import DeleteAccountModal from 'components/modal/delete_account_modal';
import EmailChangedMessage from './email_changed_message';
import Spinner from 'components/spinner';
import PageTitle from 'components/common/page_title';
import SettingsPassword from './settings_password';
import Stack, { gaps } from 'components/layout/stack';
import SettingsUserImage from './settings_user_image';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.lg};
`;

const Fields = styled.div`

`;

@observer
class SettingsUserProfileWhitelabel extends React.Component {
  @observable formLoaded = false;
  @observable showConfirmDeleteModal = false;
  @observable previousEmail = null;
  @observable emailHasChanged = false;
  @observable emailError = null;

  constructor(props) {
    super(props);
    if (!AppUI.levelStore.hasIndexData) {
      AppUI.levelStore.getIndex();
    }
    when(() => {
      if (AppUI.userIsLoaded) {
        this.formLoaded = true;
        this.previousEmail = AppUI.user.email;
        this.form = new FormFactory(AppUI.authUserStore, {
          ...this.props,
          onAfterUpdate: this.onAfterUpdate,
        });
      }
    });
  }

  onEmailChange = (field, value) => {
    this.form.onChange(field, value);
    this.emailError = null;
  };

  onAfterUpdate = async response => {
    if (response && response.failure) {
      this.emailError = response.message;
      if (this.form.store.formCache.email !== this.previousEmail) {
        this.form.store.setFormCacheValue('email', this.previousEmail);
        this.form.store.pristineFormCache.email = this.previousEmail;
      }
      return;
    }

    this.emailError = null;
    await AppUI.loadUserPromise();
    this.emailHasChanged =
      this.form.store.formCache.email &&
      this.previousEmail &&
      this.form.store.formCache.email !== this.previousEmail;

    if (this.props.onAfterUpdate) {
      this.props.onAfterUpdate(response);
    }
  };

  @computed get streakExtendGoal() { // see user.rb
    if (isBlank(AppUI.authUserStore?.formCache?.dailyXpGoal)) { return AppUI.user?.streakExtendGoal; }
    return Math.max(100, AppUI.authUserStore?.formCache?.dailyXpGoal * 2);
  }

  fields() {
    return (
      <Fields>
        {
          AppUI.authUserStore.dirtyForm &&
            <Alert
              textAlign='center'
            >
              You have unsaved changes.
            </Alert>
        }
        {
          this.emailError &&
            <Alert
              textAlign='center'
              error
            >
              That email address was already taken.
            </Alert>
        }
        {
          isPresent(AppUI.user?.email) && !AppUI.user.email.endsWith('@apple.com') && ! AppUI.user.email.endsWith('privaterelay.appleid.com') &&
            this.form.input({
              type: 'email',
              required: true,
              margin: '0 0 20px 0',
              name: 'email',
              label: 'Your Email',
              onChange: this.onEmailChange,
            })
        }
        { this.form.input({ margin: '0 0 20px 0', name: 'name', label: 'Your name', maxLength: Constants.MAX_NAME_LENGTH }) }
        <SettingsUserLanguage form={this.form} />
      </Fields>
    );
  }

  render() {
    if (this.emailHasChanged) {
      return (
        <Wrapper>
          <EmailChangedMessage email={this.form.store.formCache.email} />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <PageTitle>User Settings</PageTitle>
        {AppUI.authUserStore.hasOutstandingRequests && <Spinner />}
        {
          this.showConfirmDeleteModal &&
            <DeleteAccountModal
              onClose={() => this.showConfirmDeleteModal = false}
              activeStripeSubscriptionExists={this.props.activeStripeSubscriptionExists}
              otherSitesWarning={this.props.otherSitesWarning}
            />
        }
        <Text heading='2'>User Settings</Text>
        <Text heading='3'>Account Information</Text>
        {
          this.formLoaded &&
            <Form
              form={this.form}
              fields={this.fields()}
              {...this.props}
            />
        }
        <SettingsPassword />
        <SettingsUserImage />
        <Stack gap='sm'>
          <Text heading='3'>
            Delete Account
          </Text>
          <OutlineButton
            selectedBackground={Theme.red}
            onClick={() => this.showConfirmDeleteModal = true}
          >
            Delete Account
          </OutlineButton>
        </Stack>
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(SettingsUserProfileWhitelabel);
